// Pièces jointes : ajout (glisser-déposer, 5 fichiers, 10 Mo), vignettes,
// visionneuse, capture d'écran. Portés de MediaUploader, MediaViewer,
// ScreenshotAttacher.
import type { JSX } from 'preact'
import { useRef, useState } from 'preact/hooks'
import type { Media } from '@feedbaacks/sdk'
import { capturePage } from '../capture'
import { useFeedbaacks } from '../context'
import { Alert, ArrowLeft, ArrowRight, Camera, External, FileText, FileVideo, Plus, X } from '../icons'
import { Spinner, useDismiss } from '../primitives'
import { cx, errorMessage } from '../util'

export const MEDIA_ACCEPT = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'video/mp4', 'video/webm', 'application/pdf']
export const MEDIA_MAX_FILES = 5
export const MEDIA_MAX_BYTES = 10 * 1024 * 1024

const kindOf = (mime: string) => (mime.startsWith('image/') ? 'image' : mime.startsWith('video/') ? 'video' : 'file')

function MediaThumb({ media }: { media: Media }): JSX.Element {
  const kind = kindOf(media.mime_type)
  if (kind === 'image') return <img src={media.url} alt="" loading="lazy" />
  if (kind === 'video') return <video src={media.url} muted playsInline preload="metadata" />
  const Icon = media.mime_type.startsWith('video/') ? FileVideo : FileText
  return (
    <span class="fb-media-file">
      <Icon size={20} />
      <span>{media.mime_type.split('/')[1] ?? 'file'}</span>
    </span>
  )
}

export function MediaUploader({ medias, onChange, disabled }: { medias: Media[]; onChange: (next: Media[]) => void; disabled?: boolean }): JSX.Element {
  const { client, t } = useFeedbaacks()
  const [pending, setPending] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  // Les envois finissent dans le désordre : la liste courante est lue par ref.
  const current = useRef(medias)
  current.current = medias
  const remaining = MEDIA_MAX_FILES - medias.length - pending.length
  const atCap = remaining <= 0

  const queue = (files: File[]) => {
    const valid: File[] = []
    for (const f of files) {
      if (!MEDIA_ACCEPT.includes(f.type)) setError(t('media_unsupported_type', { type: f.type || '?' }))
      else if (f.size > MEDIA_MAX_BYTES) setError(t('media_too_large', { name: f.name }))
      else valid.push(f)
    }
    const take = valid.slice(0, Math.max(0, remaining))
    if (valid.length > take.length) setError(t('media_max_reached', { max: MEDIA_MAX_FILES }))
    for (const file of take) {
      const id = `${file.name}-${Math.random()}`
      setPending((p) => [...p, id])
      client.feedback
        .uploadMedia(file, file.name)
        .then((m) => onChange([...current.current, m]))
        .catch((err: unknown) => setError(errorMessage(err, t('media_upload_failed'))))
        .finally(() => setPending((p) => p.filter((x) => x !== id)))
    }
  }

  return (
    <div
      class="fb-media-uploader"
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled && !atCap) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled && !atCap && e.dataTransfer) queue(Array.from(e.dataTransfer.files))
      }}
    >
      {(medias.length > 0 || pending.length > 0) && (
        <div class="fb-media-grid">
          {medias.map((m) => (
            <div key={m.id} class="fb-media-tile" data-testid="feedbaacks-media-tile">
              <MediaThumb media={m} />
              {!disabled && (
                <button type="button" class="fb-media-remove" aria-label={t('media_remove')} onClick={() => onChange(medias.filter((x) => x.id !== m.id))}>
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
          {pending.map((id) => (
            <div key={id} class="fb-media-tile is-pending" data-testid="feedbaacks-media-pending">
              <Spinner size={20} />
            </div>
          ))}
        </div>
      )}
      <button type="button" class={cx('fb-dropzone', dragging && 'is-dragging')} disabled={disabled || atCap} onClick={() => input.current?.click()}>
        <Plus size={14} />
        <span class="fb-strong">{atCap ? t('media_max_reached', { max: MEDIA_MAX_FILES }) : t('media_add')}</span>
        {!atCap && <span class="fb-faint">{t('media_drop_hint')}</span>}
      </button>
      <input
        ref={input}
        type="file"
        hidden
        multiple
        accept={MEDIA_ACCEPT.join(',')}
        data-testid="feedbaacks-media-input"
        onChange={(e) => {
          const el = e.currentTarget
          if (el.files) queue(Array.from(el.files))
          el.value = ''
        }}
      />
      {error && (
        <div role="alert" class="fb-inline-error">
          <Alert size={12} />
          <span>{error}</span>
          <button type="button" aria-label={t('media_dismiss_error')} onClick={() => setError(null)}>
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

/** Vignettes cliquables + visionneuse plein écran (images, vidéos, PDF). */
export function MediaViewer({ medias }: { medias: Media[] }): JSX.Element | null {
  const { t } = useFeedbaacks()
  const [index, setIndex] = useState<number | null>(null)
  const box = useRef<HTMLDivElement>(null)
  useDismiss(index !== null, () => setIndex(null), box)
  if (medias.length === 0) return null
  const active = index !== null ? medias[index] : null
  return (
    <div class="fb-media-viewer">
      <div class="fb-media-thumbs">
        {medias.map((m, i) => (
          <button key={m.id} type="button" class="fb-media-thumb" aria-label={t('media_open_lightbox')} onClick={() => setIndex(i)} data-testid="feedbaacks-media-thumb">
            <MediaThumb media={m} />
          </button>
        ))}
      </div>
      {active && (
        <div class="fb-lightbox" role="dialog" aria-modal="true" aria-label={t('media_open_lightbox')}>
          <div class="fb-lightbox-inner" ref={box}>
            <div class="fb-lightbox-bar">
              <a href={active.url} target="_blank" rel="noreferrer noopener" class="fb-link">
                <External size={14} /> {t('media_open_original')}
              </a>
              <button type="button" class="fb-icon-btn" aria-label={t('media_close_lightbox')} onClick={() => setIndex(null)}>
                <X size={16} />
              </button>
            </div>
            <div class="fb-lightbox-stage">
              {kindOf(active.mime_type) === 'image' && <img src={active.url} alt="" />}
              {kindOf(active.mime_type) === 'video' && <video src={active.url} controls autoPlay playsInline />}
              {kindOf(active.mime_type) === 'file' && <iframe src={active.url} title={t('media_open_pdf')} />}
            </div>
            {medias.length > 1 && (
              <div class="fb-lightbox-nav">
                <button type="button" class="fb-icon-btn" aria-label={t('previous')} onClick={() => setIndex((i) => ((i ?? 0) + medias.length - 1) % medias.length)}>
                  <ArrowLeft size={16} />
                </button>
                <span>{(index ?? 0) + 1} / {medias.length}</span>
                <button type="button" class="fb-icon-btn" aria-label={t('next')} onClick={() => setIndex((i) => ((i ?? 0) + 1) % medias.length)}>
                  <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Bouton « joindre une capture » (équipe) ou vignette de la capture jointe. */
export function ScreenshotAttacher({ url, onChange }: { url: string | null; onChange: (url: string | null) => void }): JSX.Element {
  const { client, t, options } = useFeedbaacks()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (url) {
    return (
      <span class="fb-shot">
        <a href={url} target="_blank" rel="noreferrer noopener" aria-label={t('screenshot_view')}>
          <img src={url} alt={t('screenshot_attached_alt')} />
        </a>
        <button type="button" class="fb-icon-btn" aria-label={t('screenshot_remove')} onClick={() => onChange(null)}>
          <X size={14} />
        </button>
      </span>
    )
  }
  return (
    <span class="fb-shot">
      <button type="button" class="fb-btn fb-btn-ghost" disabled={busy} data-testid="feedbaacks-screenshot"
        onClick={async () => {
          setBusy(true)
          setError(null)
          try {
            onChange(await client.feedback.uploadScreenshot(await (options.capture?.takeScreenshot ?? capturePage)()))
          } catch (err) {
            setError(errorMessage(err, t('screenshot_failed')))
          } finally {
            setBusy(false)
          }
        }}>
        {busy ? <Spinner size={14} /> : <Camera size={14} />}
        {busy ? t('screenshot_capturing') : t('screenshot_attach')}
      </button>
      {error && <span role="alert" class="fb-inline-error">{error}</span>}
    </span>
  )
}
